import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Trash,
  Plus,
  Buildings,
  Sparkle,
  Spinner,
  ShieldCheck,
  PencilSimple,
  X,
  Stamp as Watermark,
} from "@phosphor-icons/react";

function WatermarkEditor({ dept, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(dept.confidentiality_label || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/departments/${dept.id}`, {
        confidentiality_label: value.trim(),
      });
      toast.success("Watermark saved");
      setEditing(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div
        className="flex items-center gap-1.5"
        data-testid={`watermark-view-${dept.id}`}
      >
        {dept.confidentiality_label ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 bg-red-50 border border-red-300 text-red-800 rounded-sm uppercase tracking-wider">
            <Watermark size={10} weight="fill" />
            {dept.confidentiality_label}
          </span>
        ) : (
          <span className="text-xs italic text-slate-400">Default</span>
        )}
        <button
          onClick={() => setEditing(true)}
          data-testid={`edit-watermark-${dept.id}`}
          className="text-xs text-slate-500 hover:text-navy inline-flex items-center gap-1"
        >
          <PencilSimple size={12} />
          edit
        </button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2" data-testid={`watermark-editor-${dept.id}`}>
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        data-testid={`watermark-input-${dept.id}`}
        placeholder="For HR purpose only"
        maxLength={40}
        className="h-8 text-xs"
      />
      <button
        onClick={save}
        disabled={saving}
        data-testid={`save-watermark-${dept.id}`}
        className="btn-navy text-xs px-2.5 py-1"
      >
        {saving ? "…" : "Save"}
      </button>
      <button
        onClick={() => {
          setEditing(false);
          setValue(dept.confidentiality_label || "");
        }}
        className="text-slate-400 hover:text-slate-700"
      >
        <X size={14} />
      </button>
    </div>
  );
}

function StandardsEditor({ dept, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState((dept.standards || []).join(", "));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const parsed = value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      await api.patch(`/departments/${dept.id}`, { standards: parsed });
      toast.success("Standards saved");
      setEditing(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div
        className="flex flex-wrap items-center gap-1.5"
        data-testid={`standards-view-${dept.id}`}
      >
        {(dept.standards || []).length === 0 ? (
          <span className="text-xs italic text-slate-400">None configured</span>
        ) : (
          dept.standards.map((s) => (
            <span
              key={s}
              className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 bg-cyan/10 border border-cyan/40 rounded-sm text-navy"
            >
              <ShieldCheck size={10} weight="fill" className="text-cyan" />
              {s}
            </span>
          ))
        )}
        <button
          onClick={() => setEditing(true)}
          data-testid={`edit-standards-${dept.id}`}
          className="text-xs text-slate-500 hover:text-navy inline-flex items-center gap-1"
        >
          <PencilSimple size={12} />
          edit
        </button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2" data-testid={`standards-editor-${dept.id}`}>
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        data-testid={`standards-input-${dept.id}`}
        placeholder="ISO 9001, IATF 16949, AS 9100"
        className="h-8 text-xs font-mono"
      />
      <button
        onClick={save}
        disabled={saving}
        data-testid={`save-standards-${dept.id}`}
        className="btn-navy text-xs px-2.5 py-1"
      >
        {saving ? "…" : "Save"}
      </button>
      <button
        onClick={() => {
          setEditing(false);
          setValue((dept.standards || []).join(", "));
        }}
        className="text-slate-400 hover:text-slate-700"
      >
        <X size={14} />
      </button>
    </div>
  );
}

export default function Departments() {
  const { user } = useAuth();
  const [deps, setDeps] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loadingAi, setLoadingAi] = useState(false);
  const canManage = user?.role === "admin";

  const load = async () => setDeps((await api.get("/departments")).data);
  useEffect(() => {
    load();
  }, []);

  const add = async (e, presetName, presetDesc) => {
    if (e) e.preventDefault();
    try {
      await api.post("/departments", {
        name: presetName || name,
        description: presetDesc || description,
      });
      if (!presetName) {
        setName("");
        setDescription("");
      }
      setSuggestions((prev) => prev.filter((s) => s.name !== (presetName || name)));
      toast.success("Department added");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this department?")) return;
    try {
      await api.delete(`/departments/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const askAi = async () => {
    setLoadingAi(true);
    try {
      const { data } = await api.post("/departments/suggest");
      setSuggestions(data.suggestions || []);
      if (!data.suggestions?.length) toast.info("Gemini couldn't think of anything new — nice coverage!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoadingAi(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="departments-page">
      <div>
        <div className="label-caps">Organization</div>
        <h2 className="font-display text-3xl text-navy uppercase mt-1">Departments</h2>
        <p className="text-sm text-slate-500 mt-1">
          Every document is tagged to a department. Admins tag each department with the industry standards it must meet — the AI benchmark checks uploads against those standards.
        </p>
      </div>

      {canManage && (
        <form onSubmit={add} className="card-flat p-5 grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-3 items-end">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={name} data-testid="dept-name-input" onChange={(e) => setName(e.target.value)} required placeholder="Quality Assurance" />
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Input value={description} data-testid="dept-desc-input" onChange={(e) => setDescription(e.target.value)} placeholder="ISO / QC / inspection" />
          </div>
          <button type="submit" data-testid="add-dept" className="btn-navy flex items-center gap-2">
            <Plus size={14} /> Add
          </button>
        </form>
      )}

      {canManage && (
        <div className="card-flat p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-caps flex items-center gap-1.5">
                <Sparkle size={14} weight="fill" className="text-cyan" /> AI suggestions
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Ask Gemini 2.5 Flash to suggest departments tuned for a vertically integrated manufacturing business — based on what you already have.
              </p>
            </div>
            <button
              onClick={askAi}
              disabled={loadingAi}
              data-testid="ai-suggest-btn"
              className="btn-cyan flex items-center gap-2 disabled:opacity-60"
            >
              {loadingAi ? <Spinner size={14} className="animate-spin" /> : <Sparkle size={14} weight="fill" />}
              {loadingAi ? "Thinking…" : "Suggest with AI"}
            </button>
          </div>

          {suggestions.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3" data-testid="ai-suggestions">
              {suggestions.map((s) => (
                <button
                  key={s.name}
                  onClick={() => add(null, s.name, s.description)}
                  data-testid={`suggest-${s.name}`}
                  className="text-xs px-3 py-1.5 border border-cyan text-navy hover:bg-cyan/10 rounded-sm transition-colors flex items-center gap-1"
                  title={s.description}
                >
                  <Plus size={12} /> {s.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="card-flat overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-zinc-200 bg-zinc-50">
              <th className="px-4 py-3 label-caps mute">Department</th>
              <th className="px-4 py-3 label-caps mute">Description</th>
              <th className="px-4 py-3 label-caps mute">Industry standards</th>
              <th className="px-4 py-3 label-caps mute">PDF watermark</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {deps.map((d) => (
              <tr
                key={d.id}
                className="dms-row border-b border-zinc-100 align-top"
                data-testid={`dept-row-${d.id}`}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 font-semibold text-navy">
                    <Buildings size={16} className="text-cyan" /> {d.name}
                  </div>
                </td>
                <td className="px-4 py-3 text-zinc-700">{d.description || "—"}</td>
                <td className="px-4 py-3">
                  {canManage ? (
                    <StandardsEditor dept={d} onSaved={load} />
                  ) : (
                    <div
                      className="flex flex-wrap gap-1.5"
                      data-testid={`standards-view-${d.id}`}
                    >
                      {(d.standards || []).length === 0 ? (
                        <span className="text-xs italic text-slate-400">None</span>
                      ) : (
                        d.standards.map((s) => (
                          <span
                            key={s}
                            className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 bg-cyan/10 border border-cyan/40 rounded-sm text-navy"
                          >
                            <ShieldCheck size={10} weight="fill" className="text-cyan" />
                            {s}
                          </span>
                        ))
                      )}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  {canManage ? (
                    <WatermarkEditor dept={d} onSaved={load} />
                  ) : (
                    <div data-testid={`watermark-view-${d.id}`}>
                      {d.confidentiality_label ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 bg-red-50 border border-red-300 text-red-800 rounded-sm uppercase tracking-wider">
                          <Watermark size={10} weight="fill" />
                          {d.confidentiality_label}
                        </span>
                      ) : (
                        <span className="text-xs italic text-slate-400">Default</span>
                      )}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {canManage && (
                    <button
                      onClick={() => del(d.id)}
                      data-testid={`delete-dept-${d.id}`}
                      className="text-zinc-400 hover:text-red-600 transition-colors"
                    >
                      <Trash size={16} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
